/**
 * S99 — el diagnóstico de la landing.
 *
 * UNA PREGUNTA A LA VEZ, NO UN FORMULARIO
 * =======================================
 * Un formulario largo se ve como trabajo y se abandona. Una pregunta sola se
 * ve como una conversación y se contesta. Es la misma información y la
 * diferencia de terminación es enorme.
 *
 * NO SE PIDE NI EL NOMBRE HASTA HABER DADO EL RESULTADO
 * ====================================================
 * Pedir el correo antes de entregar algo es la forma más rápida de perder a
 * alguien que apenas está viendo. Primero la cotización, después los datos —y
 * para entonces ya hay una razón para dejarlos—.
 *
 * LAS ADVERTENCIAS SE ENSEÑAN, NO SE ESCONDEN
 * ===========================================
 * Cuando el motor dice «esto no te conviene», eso va arriba y con su propio
 * color. Un asesor que a veces te resta es creíble; uno que siempre te suma es
 * un vendedor, y se nota.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle, ArrowLeft, ArrowRight, Check, CircleCheckBig,
  Loader2, ShieldCheck, Sparkles, TrendingDown,
} from 'lucide-react';

// Con `@/` y no con ruta relativa: el guardia de render sustituye `@/lib/api`
// por un suplente que no sale a la red. Con `../lib/api` la pantalla montaba
// contra el cliente real, la peticion nunca resolvia, y el guardia daba luz
// verde sobre una pantalla que se quedo en «Preparando...» — el peor resultado
// posible: verde sin haber probado nada.
import TurnstileWidget from '@/components/auth/TurnstileWidget';
import api from '@/lib/api';

/** El diagnóstico vive en su propia URL para poder mandarla por WhatsApp. */
export const RUTA_DIAGNOSTICO = '/diagnostico';

function Dinero({ valor, moneda = 'MXN' }) {
  const n = Number(valor);
  if (!Number.isFinite(n)) return null;
  return (
    <>
      {n.toLocaleString('es-MX', {
        style: 'currency', currency: moneda, maximumFractionDigits: 0,
      })}
    </>
  );
}

function Progreso({ hecho, total }) {
  const pct = total > 0 ? Math.round((hecho / total) * 100) : 0;
  return (
    <div className="diag__progreso" aria-hidden="true">
      <div className="diag__progreso-barra" style={{ width: `${pct}%` }} />
    </div>
  );
}

/** Una pregunta. El tipo decide el control; la ayuda va siempre visible. */
function Pregunta({ def, valor, onCambio, esGatillo }) {
  if (esGatillo) {
    const elegido = Boolean(valor);
    return (
      <div className="diag__gatillo">
        <p className="diag__pregunta">{def.pregunta}</p>
        <p className="diag__ayuda">{def.porque}</p>
        <div className="diag__si-no">
          <button
            type="button"
            className={`diag__opcion ${elegido ? 'diag__opcion--activa' : ''}`}
            onClick={() => onCambio(true)}
          >
            <Check size={16} /> Sí, me interesa
          </button>
          <button
            type="button"
            className={`diag__opcion ${valor === false ? 'diag__opcion--activa' : ''}`}
            onClick={() => onCambio(false)}
          >
            Por ahora no
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="diag__campo">
      <p className="diag__pregunta">
        {def.pregunta}
        {!def.obligatoria && <span className="diag__opcional"> (opcional)</span>}
      </p>
      {def.ayuda && <p className="diag__ayuda">{def.ayuda}</p>}

      {def.tipo === 'opciones' && (
        <div className="diag__opciones">
          {def.opciones.map((o) => (
            <button
              key={o.valor} type="button"
              className={`diag__opcion ${valor === o.valor ? 'diag__opcion--activa' : ''}`}
              onClick={() => onCambio(o.valor)}
            >
              {o.texto}
            </button>
          ))}
        </div>
      )}

      {def.tipo === 'multiple' && (
        <div className="diag__opciones">
          {def.opciones.map((o) => {
            const marcadas = Array.isArray(valor) ? valor : [];
            const activa = marcadas.includes(o.valor);
            return (
              <button
                key={o.valor} type="button"
                className={`diag__opcion ${activa ? 'diag__opcion--activa' : ''}`}
                onClick={() => onCambio(
                  activa ? marcadas.filter((x) => x !== o.valor) : [...marcadas, o.valor],
                )}
              >
                {activa && <Check size={14} />} {o.texto}
              </button>
            );
          })}
        </div>
      )}

      {def.tipo === 'numero' && (
        <input
          type="number" min={def.minimo ?? 0} inputMode="numeric"
          className="diag__numero"
          value={valor ?? ''}
          onChange={(e) => onCambio(e.target.value === '' ? null : Number(e.target.value))}
          placeholder="Escribe un número"
        />
      )}
    </div>
  );
}

/** El aviso de entrada.
 *
 * No es un adorno: es donde se responde la pregunta que todo el mundo se hace
 * antes de contestar algo en internet —«¿esto para qué es y qué me va a costar?»—.
 * Se dice el número de preguntas, se dice lo que NO se pregunta, y se dice el
 * trato completo antes de la primera pregunta. Una promesa que se cumple al
 * final vale menos que una que se explica al principio.
 */
function AvisoDeEntrada({ preguntas, onEmpezar }) {
  return (
    <div className="diag__aviso" role="dialog" aria-modal="true" data-testid="diagnostico-aviso">
      <div className="diag__aviso-caja">
        <Sparkles className="diag__aviso-icono" />
        <h2>Vamos a armar tu paquete, no a venderte uno</h2>
        <p>
          Son {preguntas} preguntas sobre <strong>cómo trabajas hoy</strong>. Ninguna
          sobre tu presupuesto: el precio sale al final como consecuencia de lo que
          necesitas, no como punto de partida.
        </p>
        <ul className="diag__aviso-lista">
          <li><Check size={16} /> Te digo qué te toca y cuánto cuesta.</li>
          <li><Check size={16} /> Te digo en pesos lo que hoy se te está yendo.</li>
          <li><Check size={16} /> Si algo no te conviene, te lo quito.</li>
        </ul>
        <div className="diag__aviso-regalo">
          <strong>Al terminar: 10 días con todo abierto.</strong>
          <span>
            Completo de verdad —no una versión recortada— y ya configurado para tu
            giro desde el primer minuto.
          </span>
        </div>
        <p className="diag__aviso-nota">
          No te pedimos ni el nombre hasta enseñarte el resultado. Toma menos de
          dos minutos.
        </p>
        <button type="button" className="button-primary" onClick={onEmpezar}>
          Empezar <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
}


/** El resultado. Lo que hoy se pierde va PRIMERO; el precio, después. */
function Resultado({ dato, onDejarDatos, enviado }) {
  if (!dato?.ok) {
    return (
      <div className="diag__resultado diag__resultado--humano">
        <h3>Esto lo tiene que ver una persona</h3>
        <p>{dato?.mensaje}</p>
        {!enviado && (
          <button type="button" className="button-primary" onClick={onDejarDatos}>
            <ArrowRight size={16} /> Dejar mis datos
          </button>
        )}
      </div>
    );
  }

  const p = dato.paquete || {};
  const dolor = dato.dolor;

  return (
    <div className="diag__resultado">
      {/* Las advertencias van arriba del precio, a propósito: lo que le QUITAMOS
          es lo que vuelve creíble lo que le dejamos. */}
      {(dato.advertencias || []).length > 0 && (
        <div className="diag__advertencias">
          <AlertTriangle size={18} />
          <div>
            <strong>Antes del precio, lo que yo no te vendería:</strong>
            <ul>
              {dato.advertencias.map((a, i) => <li key={i}>{a}</li>)}
            </ul>
          </div>
        </div>
      )}

      {dolor && (
        <div className="diag__dolor">
          <TrendingDown size={20} />
          <div>
            <p className="diag__dolor-titulo">Lo que hoy se te está yendo</p>
            <p className="diag__dolor-cifra">
              <Dinero valor={dolor.dinero_al_mes} moneda={p.moneda || 'MXN'} /> al mes
            </p>
            {dolor.ventas_al_mes && (
              <p className="diag__dolor-detalle">
                Son unas {Number(dolor.ventas_al_mes).toFixed(1)} ventas al mes que no se cierran
                porque nadie alcanzó a contestar a tiempo.
              </p>
            )}
            {dolor.supuesto && <p className="diag__supuesto">{dolor.supuesto}</p>}
          </div>
        </div>
      )}

      <div className="diag__paquete">
        <p className="diag__paquete-etiqueta">Lo que necesitas</p>
        <h3>{p.nombre}</h3>
        <p className="diag__precio">
          <Dinero valor={p.precio} moneda={p.moneda} />
          <span className="diag__periodo"> / mes</span>
        </p>
        {(p.incluye_addons || []).length > 0 && (
          <ul className="diag__addons">
            {p.incluye_addons.map((a) => (
              <li key={a.id}><Check size={14} /> {a.nombre}</li>
            ))}
          </ul>
        )}
        {dato.prueba_gratis && (
          <p className="diag__prueba">
            <Sparkles size={14} /> Incluye {dato.dias_de_prueba} días de prueba.
          </p>
        )}
        {dato.te_factura && (
          <p className="diag__factura">
            Te lo {dato.documento === 'factura' ? 'factura' : 'cobra'} {dato.te_factura}
            {dato.documento === 'factura' ? ' con CFDI.' : '.'}
          </p>
        )}
      </div>

      {(dato.porque || []).length > 0 && (
        <div className="diag__porque">
          <p className="diag__porque-titulo">Por qué esto y no otra cosa</p>
          <ul>
            {dato.porque.map((r, i) => <li key={i}><Check size={14} /> {r}</li>)}
          </ul>
        </div>
      )}

      {!enviado && (
        <button type="button" className="button-primary diag__cta" onClick={onDejarDatos}>
          <ArrowRight size={16} /> Quiero empezar
        </button>
      )}
    </div>
  );
}

export default function LandingDiagnosis() {
  const [secciones, setSecciones] = useState([]);
  const [paso, setPaso] = useState(0);
  const [respuestas, setRespuestas] = useState({ canales: [], gatillos: [] });
  const [estado, setEstado] = useState('preguntando'); // preguntando|calculando|listo|error
  const [resultado, setResultado] = useState(null);
  const [error, setError] = useState('');
  const [token, setToken] = useState(null);
  const captchaRef = useRef(null);

  const [pidiendoDatos, setPidiendoDatos] = useState(false);
  const [datos, setDatos] = useState({ nombre: '', email: '', telefono: '', empresa: '', website: '' });
  const [tokenDatos, setTokenDatos] = useState(null);
  const [enviado, setEnviado] = useState(false);
  const captchaDatosRef = useRef(null);

  // El aviso se enseña una vez por visita. Si el prospecto vuelve del pago de
  // Stripe, NO se le vuelve a enseñar: ya lo leyó y ahora espera su cuenta.
  const [avisoVisto, setAvisoVisto] = useState(
    () => new URLSearchParams(window.location.search).has('activar'),
  );
  const [tokenTarjeta, setTokenTarjeta] = useState(null);
  const [yendoAStripe, setYendoAStripe] = useState(false);
  const [cuentaLista, setCuentaLista] = useState(null);
  const captchaTarjetaRef = useRef(null);

  useEffect(() => {
    api.get('/public/diagnostico/preguntas')
      .then((r) => setSecciones(r.data?.secciones || []))
      .catch(() => setError('No pudimos cargar el diagnóstico. Recarga la página.'));
  }, []);

  // Al volver de Stripe. Se le pide al servidor que active — y el servidor le
  // pregunta a Stripe si la tarjeta existe de verdad. Nadie se cuela escribiendo
  // esta dirección a mano.
  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get('activar');
    if (!id) return;
    setEstado('activando');
    api.post('/public/diagnostico/activar', { diagnostico_id: id })
      .then((r) => {
        if (r.data?.ok) setCuentaLista(r.data);
        else setError(r.data?.mensaje || 'Todavía no vemos tu tarjeta.');
        setEstado('listo');
      })
      .catch((err) => {
        setError(err?.response?.data?.detail || 'No pudimos activar tu cuenta.');
        setEstado('listo');
      });
  }, []);

  // Se aplana el cuestionario a una lista de pasos: una pregunta por pantalla.
  const pasos = useMemo(() => {
    const salida = [];
    secciones.forEach((s) => {
      (s.preguntas || []).forEach((p) => {
        salida.push({ seccion: s.seccion, def: p, esGatillo: Boolean(s.es_gatillo) });
      });
    });
    return salida;
  }, [secciones]);

  const actual = pasos[paso];

  const valorDe = (def, esGatillo) => {
    if (esGatillo) {
      const g = respuestas.gatillos || [];
      if (g.includes(def.clave)) return true;
      return respuestas[`_no_${def.clave}`] ? false : null;
    }
    return respuestas[def.clave];
  };

  const cambiar = (def, esGatillo, valor) => {
    setRespuestas((prev) => {
      if (!esGatillo) return { ...prev, [def.clave]: valor };
      const g = new Set(prev.gatillos || []);
      if (valor === true) g.add(def.clave); else g.delete(def.clave);
      return { ...prev, gatillos: [...g], [`_no_${def.clave}`]: valor === false };
    });
  };

  const puedeAvanzar = () => {
    if (!actual) return false;
    if (actual.esGatillo) return valorDe(actual.def, true) !== null;
    if (!actual.def.obligatoria) return true;
    const v = respuestas[actual.def.clave];
    if (Array.isArray(v)) return v.length > 0;
    return v !== undefined && v !== null && v !== '';
  };

  const enviar = async () => {
    setEstado('calculando');
    setError('');
    // Las marcas `_no_*` son de la pantalla, no del motor: se quitan antes de
    // mandarlas para no ensuciar lo que se guarda del prospecto.
    const limpio = Object.fromEntries(
      Object.entries(respuestas).filter(([k]) => !k.startsWith('_no_')),
    );
    try {
      const r = await api.post('/public/diagnostico', {
        ...limpio, turnstile_token: token, page_url: window.location.href,
      });
      setResultado(r.data);
      setEstado('listo');
    } catch (err) {
      setEstado('error');
      setError(err?.response?.data?.detail || 'No se pudo calcular. Intenta de nuevo.');
      captchaRef.current?.reset();
    }
  };

  const irAStripe = async () => {
    setYendoAStripe(true);
    setError('');
    try {
      const r = await api.post('/public/diagnostico/tarjeta', {
        diagnostico_id: resultado?.diagnostico_id, turnstile_token: tokenTarjeta,
      });
      // Se sale del sitio a la pagina de Stripe: el numero de tarjeta nunca pasa
      // por nuestro servidor. Es la razon de usar su pagina y no un campo propio.
      window.location.href = r.data.url;
    } catch (err) {
      setYendoAStripe(false);
      setError(err?.response?.data?.detail || 'No se pudo abrir la página de pago.');
      captchaTarjetaRef.current?.reset();
    }
  };

  const mandarDatos = async (e) => {
    e.preventDefault();
    if (!datos.nombre.trim() || (!datos.email.trim() && !datos.telefono.trim())) {
      setError('Necesitamos tu nombre y un correo o un teléfono.');
      return;
    }
    try {
      await api.post('/public/diagnostico/contacto', {
        ...datos, diagnostico_id: resultado?.diagnostico_id, turnstile_token: tokenDatos,
      });
      setEnviado(true);
      setPidiendoDatos(false);
    } catch (err) {
      setError(err?.response?.data?.detail || 'No se pudo enviar. Intenta de nuevo.');
      captchaDatosRef.current?.reset();
    }
  };

  if (error && !pasos.length) {
    return <section className="diag"><p className="diag__error">{error}</p></section>;
  }

  // La cuenta ya existe. Es lo primero que se comprueba: si vuelve a entrar por
  // el enlace de Stripe, ve su cuenta y no el cuestionario otra vez.
  if (cuentaLista) {
    return (
      <section className="diag" data-testid="diagnostico-cuenta-lista">
        <div className="diag__resultado diag__resultado--gracias">
          <CircleCheckBig size={44} />
          <h3>Tu cuenta está lista</h3>
          <p>
            Tienes <strong>{cuentaLista.dias_de_prueba} días con todo abierto</strong>,
            ya configurado para tu giro. No te hemos cobrado nada: tu tarjeta queda
            guardada y el primer cobro es hasta el día {cuentaLista.dias_de_prueba + 1}.
          </p>
          {cuentaLista.clave_temporal && (
            <div className="diag__acceso">
              <p className="diag__acceso-titulo">Tus datos para entrar</p>
              <p>Correo: <strong>{cuentaLista.correo}</strong></p>
              <p>
                Contraseña temporal: <code>{cuentaLista.clave_temporal}</code>
              </p>
              <p className="diag__acceso-nota">
                Anótala ahora: por seguridad no la volvemos a mostrar. La cambias
                al entrar.
              </p>
            </div>
          )}
          <a className="button-primary" href="/login">
            Entrar a mi cuenta <ArrowRight size={16} />
          </a>
        </div>
      </section>
    );
  }

  if (!avisoVisto && pasos.length > 0) {
    return (
      <section className="diag">
        <AvisoDeEntrada preguntas={pasos.length} onEmpezar={() => setAvisoVisto(true)} />
      </section>
    );
  }

  // Ya dejo sus datos. Ahora el paso que convierte un interesado en un cliente:
  // la tarjeta. NO se le cobra —se valida y se guarda—, y se dice con todas sus
  // letras, porque la desconfianza en este punto es lo que tumba la conversion.
  if (enviado) {
    return (
      <section className="diag" data-testid="diagnostico-tarjeta">
        <div className="diag__resultado diag__resultado--gracias">
          <CircleCheckBig size={40} />
          <h3>Ya casi. Falta un paso.</h3>
          <p>
            Para abrirte los <strong>10 días completos</strong> necesitamos una
            tarjeta a tu nombre. Es la única forma que tenemos de saber que hay un
            negocio real del otro lado.
          </p>
          <div className="diag__sin-cobro">
            <ShieldCheck size={20} />
            <div>
              <strong>No te vamos a cobrar nada hoy.</strong>
              <span>
                Ni un peso. Tu banco puede mandarte un aviso de verificación por
                $0 que se cae solo. El primer cobro es hasta el día 11, y te
                avisamos antes — si cancelas durante la prueba, no pagas nada.
              </span>
            </div>
          </div>
          <TurnstileWidget
            ref={captchaTarjetaRef} action="landing_diagnostico_tarjeta"
            onToken={setTokenTarjeta}
          />
          {error && <p className="diag__error">{error}</p>}
          <button
            type="button" className="button-primary"
            onClick={irAStripe} disabled={yendoAStripe}
          >
            {yendoAStripe
              ? <><Loader2 size={16} className="animate-spin" /> Abriendo…</>
              : <>Activar mis 10 días <ArrowRight size={16} /></>}
          </button>
          <p className="diag__nota">
            La tarjeta la captura Stripe en su propia página. Sus datos nunca pasan
            por nuestros servidores.
          </p>
        </div>
      </section>
    );
  }

  if (estado === 'listo') {
    return (
      <section className="diag" data-testid="diagnostico-resultado">
        <Resultado
          dato={resultado}
          enviado={enviado}
          onDejarDatos={() => setPidiendoDatos(true)}
        />

        {pidiendoDatos && (
          <form className="diag__datos" onSubmit={mandarDatos} data-testid="diagnostico-datos">
            <p className="diag__datos-titulo">¿A quién le mandamos esto?</p>
            <input
              type="text" name="website" value={datos.website} tabIndex={-1}
              autoComplete="off" aria-hidden="true" className="diag__honeypot"
              onChange={(e) => setDatos((p) => ({ ...p, website: e.target.value }))}
            />
            <input
              placeholder="Tu nombre" value={datos.nombre} required
              onChange={(e) => setDatos((p) => ({ ...p, nombre: e.target.value }))}
            />
            <input
              placeholder="Nombre de tu negocio" value={datos.empresa}
              onChange={(e) => setDatos((p) => ({ ...p, empresa: e.target.value }))}
            />
            <div className="diag__datos-fila">
              <input
                type="email" placeholder="Correo" value={datos.email}
                onChange={(e) => setDatos((p) => ({ ...p, email: e.target.value }))}
              />
              <input
                type="tel" placeholder="WhatsApp" value={datos.telefono}
                onChange={(e) => setDatos((p) => ({ ...p, telefono: e.target.value }))}
              />
            </div>
            <TurnstileWidget
              ref={captchaDatosRef} action="landing_diagnostico_contacto"
              onToken={setTokenDatos}
            />
            {error && <p className="diag__error">{error}</p>}
            <button type="submit" className="button-primary">
              <ArrowRight size={16} /> Enviar
            </button>
          </form>
        )}
      </section>
    );
  }

  if (!actual) {
    return (
      <section className="diag">
        <p className="diag__cargando"><Loader2 className="animate-spin" /> Preparando…</p>
      </section>
    );
  }

  const ultimo = paso === pasos.length - 1;

  return (
    <section className="diag" data-testid="diagnostico">
      <Progreso hecho={paso} total={pasos.length} />
      <p className="diag__seccion">{actual.seccion}</p>

      <Pregunta
        def={actual.def}
        esGatillo={actual.esGatillo}
        valor={valorDe(actual.def, actual.esGatillo)}
        onCambio={(v) => cambiar(actual.def, actual.esGatillo, v)}
      />

      {/* El captcha se monta desde el principio para que ya tenga su token
          cuando el prospecto llegue al final. Resolverlo al terminar de
          contestar es exactamente donde se abandona. */}
      <div className={ultimo ? '' : 'diag__captcha-oculto'}>
        <TurnstileWidget ref={captchaRef} action="landing_diagnostico" onToken={setToken} />
      </div>

      {error && <p className="diag__error">{error}</p>}

      <div className="diag__pie">
        {paso > 0 && (
          <button type="button" className="diag__atras" onClick={() => setPaso(paso - 1)}>
            <ArrowLeft size={16} /> Atrás
          </button>
        )}
        <button
          type="button" className="button-primary"
          disabled={!puedeAvanzar() || estado === 'calculando'}
          onClick={() => (ultimo ? enviar() : setPaso(paso + 1))}
        >
          {estado === 'calculando'
            ? <><Loader2 size={16} className="animate-spin" /> Calculando…</>
            : <>{ultimo ? 'Ver mi diagnóstico' : 'Siguiente'} <ArrowRight size={16} /></>}
        </button>
      </div>

      <p className="diag__nota">
        Son {pasos.length} preguntas y toma menos de dos minutos. No pedimos ningún
        dato tuyo hasta enseñarte el resultado.
      </p>
    </section>
  );
}
